// src/privacy/pages/PrivacyPolicy.tsx
import React from "react";
import "./policy-pages.css";

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="policy-container">
      <main className="policy-card">
        <header className="policy-header">
          <h1 className="policy-title">Privacy Policy</h1>
          <p className="policy-subtitle">
            This policy explains what personal data we collect, why we collect it, and your rights.
          </p>
          <p className="policy-meta">Last updated: {new Date().toLocaleDateString()}</p>
        </header>

        <section className="policy-section">
          <h2>Overview</h2>
          <p>
            We respect your privacy and are committed to protecting your personal data. This Privacy
            Policy describes how we collect, use, share, and safeguard information when you use our
            website and application.
          </p>
        </section>

        <section className="policy-section">
          <h2>Information we collect</h2>
          <ul className="policy-list">
            <li>
              <strong>Account information:</strong> such as name, email address, and login details.
            </li>
            <li>
              <strong>Profile information:</strong> information you choose to provide (e.g.
              organisation, location).
            </li>
            <li>
              <strong>Usage data:</strong> information about how you use the app (e.g. pages viewed,
              actions taken, device/browser details).
            </li>
            <li>
              <strong>Payment information:</strong> if you subscribe, payments are processed by our
              payment provider. We do not store full payment card details; we may store identifiers
              needed to manage subscriptions (e.g. customer ID).
            </li>
            <li>
              <strong>Support communications:</strong> messages and information you send when you
              request support.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>How we use your information</h2>
          <ul className="policy-list">
            <li>Provide, operate, and maintain the service.</li>
            <li>Create and manage your account.</li>
            <li>Process subscriptions and manage billing.</li>
            <li>Improve features, performance, and user experience.</li>
            <li>Communicate important service updates (e.g. security or account notices).</li>
            <li>Respond to support requests.</li>
            <li>Comply with legal obligations and prevent fraud or abuse.</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>Legal bases for processing (EEA/UK)</h2>
          <p>
            If you are located in the EEA/UK, we process personal data under one or more of the
            following legal bases:
          </p>
          <ul className="policy-list">
            <li>
              <strong>Contract:</strong> to provide the service you request.
            </li>
            <li>
              <strong>Legitimate interests:</strong> to improve and secure the service, where those
              interests are not overridden by your rights.
            </li>
            <li>
              <strong>Consent:</strong> for optional cookies/analytics/marketing where required.
            </li>
            <li>
              <strong>Legal obligation:</strong> to comply with applicable laws.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>Sharing your information</h2>
          <p>
            We may share data with trusted service providers who help us operate the platform (for
            example hosting, analytics—when enabled, and payment processing). These providers are
            permitted to process data only to perform services on our behalf and under appropriate
            safeguards.
          </p>
          <p>
            We may also disclose information if required to do so by law or to protect the rights,
            safety, and security of our users and the service.
          </p>
        </section>

        <section className="policy-section">
          <h2>Data retention</h2>
          <p>
            We retain personal data only as long as necessary for the purposes described in this
            policy, including to comply with legal, accounting, or reporting requirements. When data
            is no longer needed, we delete or anonymize it.
          </p>
        </section>

        <section className="policy-section">
          <h2>Your rights</h2>
          <p>
            Depending on your location, you may have rights including access, correction, deletion,
            portability, and objection/restriction to processing. You may also withdraw consent at
            any time for processing based on consent.
          </p>
          <p>
            To exercise your rights, contact us via the support channel listed in the app or on our
            website.
          </p>
        </section>

        <section className="policy-section">
          <h2>Security</h2>
          <p>
            We implement reasonable technical and organizational measures to protect your
            information. However, no online service can be guaranteed 100% secure.
          </p>
        </section>

        <section className="policy-section">
          <h2>International transfers</h2>
          <p>
            Your information may be processed in countries outside your own. Where required, we use
            appropriate safeguards for international transfers (such as standard contractual
            clauses).
          </p>
        </section>

        <section className="policy-section">
          <h2>Changes to this policy</h2>
          <p>
            We may update this policy from time to time. If changes are significant, we will provide
            notice in the app or on the website.
          </p>
        </section>

        <section className="policy-section">
          <h2>Contact</h2>
          <p>
            If you have questions about this Privacy Policy, contact us via the support channel
            listed in the app or on our website.
          </p>
        </section>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
